import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, XCircle, Clock, User, Info } from "lucide-react";
import {
  listChangeRequests,
  reviewChangeRequest,
} from "./employeeBankingApi";
import { bankApiError, toDisplayString } from "./bankErrorUtils";
import ErrorBoundary from "../../components/ErrorBoundary";

const STATUS_LABEL = {
  pending: { color: "#854d0e", bg: "#fef3c7", label: "Pending" },
  approved: { color: "#166534", bg: "#dcfce7", label: "Approved" },
  rejected: { color: "#991b1b", bg: "#fee2e2", label: "Rejected" },
  withdrawn: { color: "#334155", bg: "#e2e8f0", label: "Withdrawn" },
};

function StatusPill({ status }) {
  const spec = STATUS_LABEL[status] || STATUS_LABEL.pending;
  return (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: 999,
        background: spec.bg,
        color: spec.color,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {spec.label}
    </span>
  );
}

function requestRowKey(req, index) {
  if (req?.request_id) return `bcr-${req.request_id}`;
  return `bcr-fallback-${index}`;
}

function BankChangeRequestQueueInner() {
  const mountedRef = useRef(true);
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [reviewing, setReviewing] = useState(null);
  const [reviewerComment, setReviewerComment] = useState("");

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listChangeRequests({ status: filter, limit: 200 });
      if (!mountedRef.current) return;
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      if (mountedRef.current) setError(bankApiError(e) || "Failed to load change requests");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (requestId, decision) => {
    if (reviewing) return;
    setReviewing({ requestId, decision });
    setError("");
    setInfo("");
    try {
      await reviewChangeRequest(requestId, {
        decision,
        reviewerComment: reviewerComment || null,
      });
      if (!mountedRef.current) return;
      setInfo(
        decision === "approved"
          ? "Change request approved and applied."
          : "Change request rejected."
      );
      setReviewerComment("");
      await load();
    } catch (e) {
      if (mountedRef.current) setError(bankApiError(e) || "Failed to review change request");
    } finally {
      if (mountedRef.current) setReviewing(null);
    }
  };

  const errorText = toDisplayString(error);
  const infoText = toDisplayString(info);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <User size={20} color="#1e40af" />
        <h2 style={{ margin: 0 }}>Bank Change Requests</h2>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {["pending", "approved", "rejected", "all"].map((s) => (
            <button
              key={`bcr-filter-${s}`}
              type="button"
              onClick={() => setFilter(s)}
              style={{
                padding: "4px 12px",
                borderRadius: 6,
                border: "1px solid #cbd5e1",
                background: filter === s ? "#2563eb" : "#fff",
                color: filter === s ? "#fff" : "#0f172a",
                cursor: "pointer",
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {errorText ? (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            background: "#fee2e2",
            color: "#991b1b",
            borderRadius: 6,
          }}
        >
          {errorText}
        </div>
      ) : null}
      {infoText ? (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            background: "#dcfce7",
            color: "#166534",
            borderRadius: 6,
          }}
        >
          {infoText}
        </div>
      ) : null}

      <div style={{ marginTop: 12 }}>
        {loading && <div style={{ color: "#64748b" }}>Loading…</div>}
        {!loading && items.length === 0 && (
          <div
            style={{
              padding: 20,
              background: "#f8fafc",
              borderRadius: 8,
              color: "#64748b",
              textAlign: "center",
            }}
          >
            No {filter === "all" ? "" : filter} change requests.
          </div>
        )}
        {items.map((req, index) => (
          <div
            key={requestRowKey(req, index)}
            style={{
              padding: 14,
              margin: "10px 0",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              background: "#fff",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <StatusPill status={req.status} />
                <span style={{ fontSize: 13, color: "#64748b" }}>
                  Employee: {toDisplayString(req.employee_id)}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                <Clock size={11} style={{ verticalAlign: "middle" }} />{" "}
                {req.created_at ? new Date(req.created_at).toLocaleString() : "—"}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 10,
                marginTop: 8,
                fontSize: 13,
              }}
            >
              <div>
                <div style={{ color: "#64748b", fontSize: 11 }}>Holder</div>
                <div>{toDisplayString(req.account_holder_name)}</div>
              </div>
              <div>
                <div style={{ color: "#64748b", fontSize: 11 }}>Account #</div>
                <div style={{ fontFamily: "monospace" }}>
                  ****{toDisplayString(req.account_number_last4)}
                </div>
              </div>
              <div>
                <div style={{ color: "#64748b", fontSize: 11 }}>Method</div>
                <div>{toDisplayString(req.payment_method)}</div>
              </div>
              <div>
                <div style={{ color: "#64748b", fontSize: 11 }}>Effective</div>
                <div>{toDisplayString(req.requested_effective_from)}</div>
              </div>
            </div>

            {req.reason ? (
              <div
                style={{
                  marginTop: 6,
                  padding: 6,
                  background: "#f8fafc",
                  borderRadius: 4,
                  fontSize: 12,
                  color: "#334155",
                }}
              >
                <Info size={11} style={{ verticalAlign: "middle" }} />{" "}
                {toDisplayString(req.reason)}
              </div>
            ) : null}

            {req.status === "pending" && (
              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <input
                  placeholder="Optional reviewer comment"
                  value={reviewerComment}
                  onChange={(e) => setReviewerComment(e.target.value)}
                  style={{
                    flex: 1,
                    padding: 6,
                    border: "1px solid #cbd5e1",
                    borderRadius: 4,
                  }}
                />
                <button
                  type="button"
                  disabled={reviewing?.requestId === req.request_id}
                  onClick={() => review(req.request_id, "approved")}
                  style={{
                    padding: "6px 12px",
                    background: "#166534",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <CheckCircle2 size={14} />
                  Approve
                </button>
                <button
                  type="button"
                  disabled={reviewing?.requestId === req.request_id}
                  onClick={() => review(req.request_id, "rejected")}
                  style={{
                    padding: "6px 12px",
                    background: "#991b1b",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <XCircle size={14} />
                  Reject
                </button>
              </div>
            )}

            {req.status !== "pending" && req.reviewer_comment ? (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  color: "#334155",
                  fontStyle: "italic",
                }}
              >
                Reviewer: {toDisplayString(req.reviewer_comment)}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BankChangeRequestQueue() {
  return (
    <ErrorBoundary
      compact
      title="Bank change requests unavailable"
      message="This queue hit an unexpected error. Refresh to try again."
    >
      <BankChangeRequestQueueInner />
    </ErrorBoundary>
  );
}
